import React, { useState, useEffect } from 'react';
import './App.css';
import { API_BASE_URL } from './config';

// --- COMPOSANT D'ICÔNE DYNAMIQUE ---
const ActionIcon = ({ name }) => {
  switch (name) {
    case 'lumiere-on':
      return (
        <svg xmlns="https://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
      );
    case 'lumiere-off':
      return (
        <svg xmlns="https://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
      );
    case 'volet':
      return (
        <svg xmlns="https://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 4H2M22 8H2M22 12H2M22 16H2M22 20H2"></path>
        </svg>
      );
    case 'portail':
      return (
        <svg xmlns="https://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" ry="2"></rect>
            <line x1="10" y1="12" x2="14" y2="12"></line>
            <line x1="10" y1="8" x2="8" y2="12"></line>
            <line x1="10" y1="16" x2="8" y2="12"></line>
            <line x1="14" y1="8" x2="16" y2="12"></line>
            <line x1="14" y1="16" x2="16" y2="12"></line>
        </svg>
      );
    default: // Icône par défaut
      return (
        <svg xmlns="https://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path><line x1="12" y1="2" x2="12" y2="12"></line>
        </svg>
      );
  }
};

const Spinner = () => <div className="spinner"></div>;

// --- COMPOSANT D'ANIMATION DE CHARGEMENT ---
const LoadingAnimation = () => (
  <div className="loading-animation">
    <div className="loading-spinner"></div>
    <div className="loading-dots">
      <div className="loading-dot"></div>
      <div className="loading-dot"></div>
      <div className="loading-dot"></div>
    </div>
    <div className="loading-text">Chargement des actions...</div>
  </div>
);

function App() {
  const [actions, setActions] = useState([]);
  const [slug, setSlug] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loadingAction, setLoadingAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const path = window.location.pathname.substring(1);
    if (!path) {
      setError('Access denied');
      return;
    }
    setSlug(path);
    setIsLoading(true);
    setError('');

    const apiUrl = `${API_BASE_URL}/api/pages/${path}`;

    fetch(apiUrl)
      .then(async response => {
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Erreur ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setIsLoading(false);
        if (data.actions && data.actions.length > 0) {
          setActions(data.actions);
          setMessage('');
        } else {
          setMessage('Aucune action n\'est configurée pour cette page.');
        }
      })
      .catch(err => {
        console.error("Erreur lors de la récupération des actions:", err);
        setIsLoading(false);
        const errorMessage = err.message === 'Failed to fetch' ? 'Impossible de contacter l\'API' : err.message;
        setError(errorMessage);
        setMessage('');
      });
  }, []);

  const handleActionClick = (actionId, actionName) => {
    setLoadingAction(actionId);
    setError('');
    setMessage('');

    const executeUrl = `${API_BASE_URL}/api/pages/${slug}/execute`;

    fetch(executeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actionId: actionId }),
    })
    .then(response => response.json().then(data => ({ ok: response.ok, body: data })))
    .then(({ ok, body }) => {
      if (ok) {
        setMessage(body.message || `Action "${actionName}" exécutée avec succès !`);
      } else {
        throw new Error(body.message || `L'exécution a échoué.`);
      }
    })
    .catch(err => {
      console.error("Erreur lors de l'exécution de l'action:", err);
      const errorMessage = err.message === 'Failed to fetch' ? 'Impossible de contacter l\'API' : err.message;
      setError(errorMessage);
    })
    .finally(() => {
      setLoadingAction(null);
    });
  };

  // --- NOUVELLE FONCTION POUR FERMER LES MESSAGES ---
  const handleCloseMessage = () => {
    setMessage('');
    setError('');
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Panneau de Contrôle</h1>
        <div className="action-container">
          {isLoading && <LoadingAnimation />}
          
          {!isLoading && actions.length === 0 && (
            <div className="message-container">
              {message && <p className="status-message">{message}</p>}
              {error && <p className="error-message">{error}</p>}
            </div>
          )}

          {!isLoading && actions.map(action => (
            <button
              key={action.id}
              onClick={() => handleActionClick(action.id, action.name)}
              className="action-button"
              disabled={loadingAction === action.id}
            >
              {loadingAction === action.id ? <Spinner /> : <ActionIcon name={action.icon} />}
              <span>{action.name}</span>
            </button>
          ))}
        </div>

        {!isLoading && actions.length > 0 && (
          <div className="message-container">
            {/* --- MODIFICATION POUR AFFICHER LE BOUTON FERMER --- */}
            {message && (
              <div className="status-message">
                <span>{message}</span>
                <button onClick={handleCloseMessage} className="close-button">&times;</button>
              </div>
            )}
            {error && (
              <div className="error-message">
                <span>{error}</span>
                <button onClick={handleCloseMessage} className="close-button">&times;</button>
              </div>
            )}
          </div>
        )}
      </header>
    </div>
  );
}

export default App;
